import { ReactNode } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { CombinationType, PortfolioProductsMap } from 'types/cartServiceV2';
import { PortfolioToCartItem } from 'store/modules/proposal/types';
import { addDynamicPortfolio, getProductsAvailableForCombining } from 'api/operations/cartServiceV2';
import { createDynamicPortfolio, createDynamicPortfolioRFQ } from 'api/operations/productServiceV2';
import { ProposalPageType } from 'global/hooks/useRFQProposalPageType';
import IProposalPageStore from '../iProposalPageStore';
import ProductLineDelegate from '../delegates/productLineDelegate';

interface CreatePortfolioFields extends DefaultFormFields {
  portfolioName: FormControl<string>;
}

interface Stores<PageStore extends IProposalPageStore> {
  pageStore: IProposalPageStore;
  productLineDelegate: ProductLineDelegate<PageStore>;
}

export interface PortfolioPresenter {
  id: string;
  name: string;
}

export default class CreatePortfolioModalStore<PageStore extends IProposalPageStore> {
  private pageStore: IProposalPageStore;
  private productLineDelegate: ProductLineDelegate<PageStore>;
  private portfolioMap: PortfolioProductsMap = {};
  private _isLoading = false;
  private availablePlansIds: string[] = [];
  private browsingType;
  private carrierIdToPlansMaxAmount: Record<string, number> = {};
  private carrierId: string | undefined = '';
  private createPortfolioCombinationType: CombinationType = 'PROPOSAL';

  private form: FormController<CreatePortfolioFields> = new FormController({
    portfolioName: new FormControl<string>('', { validations: ['printableCharacters'], trim: true }),
  });

  private filterPortfolios(param: boolean): string[] {
    return Object.entries(this.portfolioMap)
      .filter(([_, value]) => value === param)
      .map((elem) => elem[0]);
  }

  private setPortfolioMap(newData: PortfolioProductsMap): void {
    this.portfolioMap = newData;
  }

  public setCarrierId(planId: string | undefined): void {
    if (!planId) {
      this.carrierId = undefined;
      return;
    }
    const carrierId = this.productLineDelegate.portfolioToCartItems.find((item) => item.portfolio.id === planId)
      ?.portfolio.products[0].carrier.id;
    this.carrierId = carrierId;
  }

  public closeModal(): void {
    this.productLineDelegate.createPortfolioModalDelegate.close();
  }

  public setLoading(status: boolean): void {
    this._isLoading = status;
  }

  public setCreatePortfolioCombinationType(createPortfolioCombinationType: CombinationType): void {
    this.createPortfolioCombinationType = createPortfolioCombinationType;
  }

  public getInitialPortfolioMap(): void {
    this.setAvailablePlanIds();
    const newPortfolioMap: PortfolioProductsMap = {};
    this.availablePlansIds.forEach((planId) => {
      newPortfolioMap[planId] = false;
    });
    this.setPortfolioMap(newPortfolioMap);
  }

  public setPortfolioName = this.form.onFieldChange;

  public async addPlan(planId: string): Promise<void> {
    this.portfolioMap[planId] = true;
    this.setCarrierId(planId);
    this.setAvailablePlanIds();
    await this.updatePortfolioMap();
  }

  public removePlan(planId: string): void {
    this.portfolioMap[planId] = false;
    if (this.inPortfolioItems.length < 1) {
      this.setCarrierId(undefined);
      this.setAvailablePlanIds();
      this.getInitialPortfolioMap();
    }
  }

  public formatPortfolios(portfolioIds: string[]): PortfolioPresenter[] {
    return portfolioIds.map((portfolioId) => {
      const cartItem = this.productLineDelegate.portfolioToCartItems.find(
        (item) => item.cartItem.portfolioId === portfolioId,
      );
      if (!cartItem) {
        throw new Error(`No cart item found with id: ${portfolioId}`);
      }
      return {
        id: portfolioId,
        name: cartItem.portfolio.name,
      };
    });
  }

  public async updatePortfolioMap(): Promise<void> {
    this.setLoading(true);
    const {
      data: { canCombineWith: newProducts },
    } = await getProductsAvailableForCombining(
      this.pageStore.cartId,
      this.productLineDelegate.productLineId,
      this.inPortfolioItems,
      this.createPortfolioCombinationType,
    );
    runInAction(() => {
      const newPortfolioMap: PortfolioProductsMap = {};
      Object.keys(newProducts).forEach((productId) => {
        if (newProducts[productId] && this.availablePlansIds.includes(productId)) {
          newPortfolioMap[productId] = false;
        }
      });
      this.inPortfolioItems.forEach((product) => {
        newPortfolioMap[product] = true;
      });
      this.setPortfolioMap(newPortfolioMap);
      this.setLoading(false);
    });
  }

  public async createPortfolio(proposalPageType: ProposalPageType): Promise<void> {
    this.setLoading(true);
    let createdDynamicPortfolioId = '';
    const simplifiedBC = proposalPageType === 'CREATE_PORTFOLIO' && this.browsingType === 'RFQ';
    // The "Create Portfolio" page type requires custom logic because the plans at this step do not include rates.
    if (proposalPageType === 'CREATE_PORTFOLIO') {
      const { data } = await createDynamicPortfolio(this.inPortfolioItems, this.portfolioName);
      createdDynamicPortfolioId = data?.createDynamicPortfolio.id || '';
    } else {
      createdDynamicPortfolioId = (await this.createDynamicPortfolioByBrowsingType()) || '';
    }

    if (createdDynamicPortfolioId) {
      await runInAction(async () => {
        const res = await addDynamicPortfolio(
          this.pageStore.cartId,
          createdDynamicPortfolioId,
          this.portfolioName,
          this.inPortfolioItems,
          simplifiedBC,
        );
        await runInAction(async () => {
          if (res.data) {
            const newCart = res.data.addDynamicPortfolio;
            await this.pageStore.updateCustomPortfolioAndCart(newCart, createdDynamicPortfolioId);
          }
        });
      });
    }
    runInAction(() => {
      this.setLoading(false);
      this.closeModal();
    });
  }

  /**
   * Uses the local browsing type variable to determine the dynamic portfolio type, then calls either RFQ or usual
   * portfolio creation. We made this method so, because there RFQ product lines may have SHELF browsing criteria.
   * Though usual proposals may not have RFQ product lines, the SHELF inside of RFQ case must be handled here buy this
   * method.
   */
  private async createDynamicPortfolioByBrowsingType(): Promise<string | undefined> {
    if (this.browsingType === 'RFQ') {
      const { data } = await createDynamicPortfolioRFQ(
        this.inPortfolioItems,
        this.portfolioName,
        this.pageStore.cartId,
        this.productLineDelegate.productLineId,
        this.inPortfolioItems.map((id) => {
          const cartItemInfo = this.productLineDelegate.portfolioToCartItems.find(
            (item) => item.cartItem.portfolioId === id,
          );
          const browsingCriteria = (cartItemInfo as PortfolioToCartItem).cartItem.browsingCriteria;
          return { portfolioId: id, browsingCriteria };
        }),
      );
      return data?.createDynamicRFQPortfolio.id;
    }
    if (this.browsingType === 'SHELF') {
      const { data } = await createDynamicPortfolio(this.inPortfolioItems, this.portfolioName);
      return data?.createDynamicPortfolio.id;
    }
    return undefined;
  }

  private setAvailablePlanIds(): void {
    this.availablePlansIds = this.productLineDelegate.portfolioToCartItems
      // availablePlansIds is a list of portfolio ids that are "single product" and only such type is available to pare products from it into the "multi product" portfolio
      .filter((item) => item.portfolio.products.length === 1)
      .filter((item) => !this.carrierId || item.portfolio.products[0].carrier.id === this.carrierId)
      .map((item) => item.portfolio.id);
  }

  public isDataValid(): boolean {
    return this.form.isFormValid() && this.inPortfolioItems.length > 1;
  }

  get inPortfolioItems(): string[] {
    return this.filterPortfolios(true);
  }

  get availableToAddItems(): string[] {
    return this.filterPortfolios(false);
  }

  get portfolioName(): string {
    return this.form.fields.portfolioName.value;
  }

  get portfolioNameError(): ReactNode {
    return this.form.fields.portfolioName.unpassedValidationMsg;
  }

  get modalIsVisible(): boolean {
    return this.productLineDelegate.createPortfolioModalDelegate.isVisible;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  public get maxProductsNumber(): number {
    if (this.carrierId) {
      return this.carrierIdToPlansMaxAmount[this.carrierId] || this.carrierIdToPlansMaxAmount.def || 0;
    }
    return 0;
  }

  constructor({ pageStore, productLineDelegate }: Stores<PageStore>) {
    this.pageStore = pageStore;
    this.productLineDelegate = productLineDelegate;
    const domainProductLine = this.pageStore.getDomainProductLine(this.productLineDelegate.productLineId);
    if (!domainProductLine) {
      throw new Error('No valid domain product line');
    }
    this.browsingType = domainProductLine.browsingType;
    this.carrierIdToPlansMaxAmount = { ...domainProductLine.dynamicPortfolios.carrierPortfolioSize };

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
